import { useCallback, useEffect, useRef } from 'react';
import { useObserverRegistry, type AnchorMeta } from './observerContext';

/*
  Регистрация элемента в мире «Наблюдателя» (docs/observer-design.md §3).

  Элементы заявляют о себе сами и снимаются с учёта при размонтировании: Наблюдатель никогда
  не хранит ссылку на исчезнувший узел. Возвращается ref-функция, её просто вешают на элемент:

    const ref = useObserverAnchor('header.notifications', { role: 'source', label: 'Уведомления' });
    <button ref={ref} … />

  Один адрес может стоять на нескольких элементах сразу (строка INC-2189 на главной и шапка его вкладки):
  снимается всегда свой элемент, а цель выбирает тот, что виден. Подписка — на реестр, а не на всё
  состояние Наблюдателя: событие не перерисовывает якоря. Без провайдера (галерея, основной сайт) ref
  ничего не делает — меню и панели живут и там.
*/

export function useObserverAnchor<T extends HTMLElement = HTMLElement>(id: string, meta: AnchorMeta) {
  const registry = useObserverRegistry();
  const metaRef = useRef(meta);
  const elRef = useRef<T | null>(null);

  useEffect(() => {
    metaRef.current = meta;
    if (elRef.current) registry?.registerAnchor(id, elRef.current, meta);
  }, [id, meta, registry]);

  useEffect(
    () => () => {
      if (elRef.current) registry?.unregisterAnchor(id, elRef.current);
    },
    [id, registry],
  );

  return useCallback(
    (el: T | null) => {
      const prev = elRef.current;
      elRef.current = el;
      if (!registry) return;
      if (prev && prev !== el) registry.unregisterAnchor(id, prev);
      if (el) registry.registerAnchor(id, el, metaRef.current);
    },
    [id, registry],
  );
}
